import axios, {parseResponseData} from '@/axiosInstance'
import {captureException} from '@sentry/react'
import {z} from 'zod'
import {DateRangeFilterEnum} from '@/types'

export const DashboardOpsResponse = z.object({
    graph: z
        .object({
            month: z.string().optional(),
            day: z.string().optional(),
            completed: z.number(),
            absences: z.number(),
            completedSum: z.number(),
            absencesSum: z.number()
        })
        .array()
        .optional(),
    accessCounters: z
        .object({
            planned: z.number(),
            completed: z.number(),
            absences: z.number()
        })
        .optional(),
    projects: z
        .object({
            renewed: z.number(),
            new: z.number(),
            closed: z.number()
        })
        .optional(),
    patients: z.number().optional(),
    patientAbsences: z
        .object({
            paid: z.number(),
            not_paid: z.number()
        })
        .optional(),
    consultantAbsences: z
        .object({
            therapist: z.object({
                absences: z.number(),
                permits: z.number()
            }),
            doctors: z.object({
                absences: z.number(),
                permits: z.number()
            })
        })
        .optional()
})

export type DashboardOpsResponse = z.infer<typeof DashboardOpsResponse>

export interface HttpGetDashboardOpsOptions {
    params: {
        fields: ('graph' | 'projects' | 'patients' | 'patientAbsences' | 'consultantAbsences')[]
        startDate?: string
        endDate?: string
        assistiveRegime?: string
        sector?: string
    }
}

export const httpGetDashboardOps = async ({params}: HttpGetDashboardOpsOptions) => {
    const response = await axios.get('dashboard/ops', {params})
    return parseResponseData({
        responseData: response.data,
        responseShape: DashboardOpsResponse,
        onZodError: captureException
    })
}

export const DashboardConsultantsResponse = z.object({
    operativeConsultants: z
        .object({
            therapists: z.object({
                domiciliary: z.number(),
                outpatient_domiciliary: z.number(),
                outpatient: z.number()
            }),
            doctors: z.object({
                flat_rate: z.number(),
                consultant: z.number()
            })
        })
        .optional(),
    hours: z
        .object({
            done: z.number(),
            planned: z.number(),
            absences: z.number()
        })
        .optional(),
    costs: z
        .object({
            total: z.number(),
            forfait: z.number()
        })
        .optional(),
    cashback: z
        .object({
            total: z.number(),
            numberOfAccesses: z.number(),
            margin: z.number()
        })
        .optional()
})

export type DashboardConsultantsResponse = z.infer<typeof DashboardConsultantsResponse>

export interface HttpGetDashboardConsultantsOptions {
    params: {
        fields: ('operativeConsultants' | 'hours' | 'costs' | 'cashback')[]
        startDate?: string
        endDate?: string
        consultant?: string
    }
}

export const httpGetDashboardConsultants = async ({params}: HttpGetDashboardConsultantsOptions) => {
    const response = await axios.get('dashboard/consultants', {params})
    return parseResponseData({
        responseData: response.data,
        responseShape: DashboardConsultantsResponse,
        onZodError: captureException
    })
}

export const DashboardAnalyticsResponse = z.object({
    graph: z
        .object({
            budgetSaturation: z.number(),
            annualBudget: z.number(),
            distribution: z.number(),
            year: z.array(
                z.object({
                    month: z.string(),
                    billed: z.number(),
                    planned: z.number(),
                    budget: z.number()
                })
            )
        })
        .optional(),
    billedAccesses: z
        .object({
            accesses: z.number(),
            absences: z.number()
        })
        .optional(),
    costs: z
        .object({
            total: z.number()
        })
        .optional(),
    otherCosts: z
        .object({
            total: z.number()
        })
        .optional()
})

export type DashboardAnalyticsResponse = z.infer<typeof DashboardAnalyticsResponse>

export interface HttpGetDashboardAnalyticsOptions {
    params: {
        fields: ('graph' | 'billedAccesses' | 'costs' | 'otherCosts')[]
        startDate?: string
        endDate?: string
        assistiveRegime?: string
        sector?: string
        range?: DateRangeFilterEnum
    }
}

export const httpGetDashboardAnalytics = async ({params}: HttpGetDashboardAnalyticsOptions) => {
    const response = await axios.get('dashboard/analytics', {params})
    return parseResponseData({
        responseData: response.data,
        responseShape: DashboardAnalyticsResponse,
        onZodError: captureException
    })
}
